import React, { useEffect, useMemo, useState } from "react";
import { IconButton } from "@material-ui/core";
import { Cached, NavigateBefore, NavigateNext } from "@material-ui/icons";
import GridContainer from "../../components/Grid/GridContainer";
import GridItem from "../../components/Grid/GridItem";
import Table from "../../components/Table/Table";
import Button from "../../components/CustomButtons/Button";
import CustomInput from "../../components/CustomInput/CustomInputValidate";
import axios from "store/axios/axios-gci";

const PAGE_SIZE = 50;
const MAX_CELL_LENGTH = 240;

const LoadingIndicator = ({ label = "Loading" }) => (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 8, color: "#666" }}>
        <style>{"@keyframes gci-loading-spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}"}</style>
        <Cached style={{ animation: "gci-loading-spin 0.9s linear infinite", fontSize: 18 }} />
        <span>{label}</span>
    </span>
);

const normalize = value => String(value == null ? "" : value).toLowerCase();

const isJsonValue = value => {
    if (typeof value !== "string") return false;
    const text = value.trim();
    if (!text || (text.charAt(0) !== "{" && text.charAt(0) !== "[")) return false;

    try {
        JSON.parse(text);
        return true;
    } catch (error) {
        return false;
    }
};

const prettifyJson = value => {
    try {
        return JSON.stringify(JSON.parse(value), null, 2);
    } catch (error) {
        return value;
    }
};

const formatCell = (value, openJsonModal) => {
    if (value == null || value === "") return "";
    const text = typeof value === "string" ? value : String(value);
    if (isJsonValue(text)) {
        return (
            <Button
                color="rose"
                size="sm"
                onClick={() => openJsonModal(prettifyJson(text))}
            >
                View JSON
            </Button>
        );
    }
    if (text.length <= MAX_CELL_LENGTH) return text;
    return (
        <span title={text}>
            {text.substring(0, MAX_CELL_LENGTH)}
            ...
        </span>
    );
};

const GCITables = () => {
    const [tables, setTables] = useState([]);
    const [selectedTable, setSelectedTable] = useState("");
    const [columns, setColumns] = useState([]);
    const [rows, setRows] = useState([]);
    const [count, setCount] = useState(0);
    const [offset, setOffset] = useState(0);
    const [search, setSearch] = useState("");
    const [loadingTables, setLoadingTables] = useState(false);
    const [loadingRows, setLoadingRows] = useState(false);
    const [error, setError] = useState("");
    const [jsonModal, setJsonModal] = useState("");

    const loadTables = async () => {
        setLoadingTables(true);
        setError("");

        try {
            const response = await axios.get("/tables");
            const nextTables = response.data && Array.isArray(response.data.tables)
                ? response.data.tables
                : [];
            setTables(nextTables);
            if (!selectedTable && nextTables.length > 0) {
                setSelectedTable(nextTables[0].name);
            }
        } catch (requestError) {
            console.error("Unable to load GCI tables", requestError);
            setError("Unable to load GCI tables.");
        } finally {
            setLoadingTables(false);
        }
    };

    const loadRows = async () => {
        if (!selectedTable) return;

        setLoadingRows(true);
        setError("");

        try {
            const response = await axios.get(`/tables/${selectedTable}`, {
                params: {
                    limit: PAGE_SIZE,
                    offset
                }
            });
            setColumns(response.data.columns || []);
            setRows(response.data.rows || []);
            setCount(response.data.count || 0);
        } catch (requestError) {
            console.error("Unable to load GCI table rows", requestError);
            setColumns([]);
            setRows([]);
            setCount(0);
            setError(
                requestError.response &&
                requestError.response.data &&
                requestError.response.data.error
                    ? requestError.response.data.error
                    : "Unable to load GCI table rows."
            );
        } finally {
            setLoadingRows(false);
        }
    };

    useEffect(() => {
        loadTables();
    }, []);

    useEffect(() => {
        loadRows();
    }, [selectedTable, offset]);

    const selectedTableMeta = tables.find(table => table.name === selectedTable);

    const filteredRows = useMemo(() => {
        const query = normalize(search.trim());
        if (!query) return rows;

        return rows.filter(row => {
            return columns.some(column => normalize(row[column]).includes(query));
        });
    }, [rows, columns, search]);

    const tableData = filteredRows.map(row => {
        return columns.map(column => formatCell(row[column], setJsonModal));
    });

    const hasPrevious = offset > 0;
    const hasNext = offset + PAGE_SIZE < count;
    const pageStart = count === 0 ? 0 : offset + 1;
    const pageEnd = Math.min(offset + PAGE_SIZE, count);

    return (
        <GridContainer>
            <GridItem xs={12} sm={12} md={12}>
                <h1>GCI Tables</h1>
            </GridItem>

            <GridItem xs={12} sm={4} md={4}>
                <div style={{ marginTop: 20 }}>
                    <select
                        value={selectedTable}
                        onChange={event => {
                            setSelectedTable(event.target.value);
                            setOffset(0);
                            setSearch("");
                        }}
                        style={{
                            width: "100%",
                            height: 40,
                            border: "1px solid #ddd",
                            borderRadius: 4,
                            background: "#fff",
                            padding: "0 10px"
                        }}
                    >
                        {tables.map(table => (
                            <option key={table.name} value={table.name}>
                                {table.label} ({table.name})
                            </option>
                        ))}
                    </select>
                </div>
            </GridItem>

            <GridItem xs={12} sm={5} md={5}>
                <CustomInput
                    onChange={event => setSearch(event.target.value)}
                    value={search}
                    label="Search Loaded Rows"
                />
            </GridItem>

            <GridItem xs={12} sm={3} md={3}>
                <div style={{ marginTop: 14, textAlign: "right" }}>
                    <IconButton disabled={loadingTables || loadingRows} onClick={() => {
                        loadTables();
                        loadRows();
                    }}>
                        <Cached />
                    </IconButton>
                    <IconButton disabled={!hasPrevious || loadingRows} onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}>
                        <NavigateBefore />
                    </IconButton>
                    <IconButton disabled={!hasNext || loadingRows} onClick={() => setOffset(offset + PAGE_SIZE)}>
                        <NavigateNext />
                    </IconButton>
                </div>
            </GridItem>

            <GridItem xs={12} sm={12} md={12}>
                <div style={{ marginBottom: 15 }}>
                    {selectedTableMeta && (
                        <span>
                            Showing {pageStart}-{pageEnd} of {count} rows from <strong>{selectedTableMeta.name}</strong>
                        </span>
                    )}
                    {(loadingTables || loadingRows) && <span style={{ marginLeft: 15 }}><LoadingIndicator /></span>}
                    {error && <div style={{ color: "#c00", marginTop: 10 }}>{error}</div>}
                </div>
            </GridItem>

            <GridItem xs={12} sm={12} md={12}>
                {columns.length > 0 && <Table tableHead={columns} tableData={tableData} />}
                {columns.length === 0 && !loadingRows && <div>No rows loaded.</div>}
            </GridItem>

            <GridItem xs={12} sm={12} md={12}>
                <div style={{ marginTop: 15 }}>
                    <Button disabled={!hasPrevious || loadingRows} onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}>
                        Previous
                    </Button>
                    <Button color="rose" disabled={!hasNext || loadingRows} onClick={() => setOffset(offset + PAGE_SIZE)}>
                        Next
                    </Button>
                </div>
            </GridItem>

            {jsonModal && (
                <div
                    style={{
                        position: "fixed",
                        top: 0,
                        right: 0,
                        bottom: 0,
                        left: 0,
                        zIndex: 1300,
                        background: "rgba(0, 0, 0, 0.45)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: 20
                    }}
                    onClick={() => setJsonModal("")}
                >
                    <div
                        style={{
                            width: "min(900px, 95vw)",
                            maxHeight: "90vh",
                            background: "#fff",
                            borderRadius: 4,
                            padding: 20,
                            boxShadow: "0 10px 35px rgba(0,0,0,0.25)"
                        }}
                        onClick={event => event.stopPropagation()}
                    >
                        <h3 style={{ marginTop: 0 }}>JSON Value</h3>
                        <textarea
                            readOnly
                            value={jsonModal}
                            style={{
                                width: "100%",
                                minHeight: "60vh",
                                fontFamily: "monospace",
                                fontSize: 13,
                                border: "1px solid #ddd",
                                borderRadius: 4,
                                padding: 12,
                                resize: "vertical"
                            }}
                            onFocus={event => event.target.select()}
                        />
                        <div style={{ marginTop: 15, textAlign: "right" }}>
                            <Button onClick={() => setJsonModal("")}>
                                Close
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </GridContainer>
    );
};

export default GCITables;
